// src/components/workspace/shell/ContextRail.js

import { useEffect, useState } from "react";
import { Check, Pencil, X } from "lucide-react";
import { getCategoryLabel, getLaneById } from "../../../constants/lanes";
import {
  CONTEXT_FIELDS,
  EMPTY_CONTEXT,
} from "../../../constants/incidentContext";

/**
 * Everything known about the incident, always available and never in the way.
 *
 * The expert should not have to ask "what environment is this?" mid-session, and
 * the customer should not have to repeat themselves. The structured context is
 * customer-editable so it can be corrected as understanding improves.
 */

const EMPTY_FORM = EMPTY_CONTEXT;

function Section({ title, children, action }) {
  return (
    <section className="border-b border-slate-200 px-4 py-3.5">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
          {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function Fact({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5">
      <dt className="flex-shrink-0 text-xs text-slate-500">{label}</dt>
      <dd className="min-w-0 truncate text-right text-xs font-medium text-slate-800">
        {value}
      </dd>
    </div>
  );
}

export default function ContextRail({
  problem,
  severity,
  viewerRole,
  onSaveContext,
  saving = false,
}) {
  const canEdit = viewerRole === "client";
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const context = problem?.context || null;

  useEffect(() => {
    setForm({
      ...EMPTY_FORM,
      ...CONTEXT_FIELDS.reduce(
        (acc, field) => ({ ...acc, [field.key]: context?.[field.key] || "" }),
        {}
      ),
    });
  }, [context]);

  const lane = getLaneById(problem?.laneId);
  const hasAnyContext = CONTEXT_FIELDS.some((f) => context?.[f.key]);

  const handleSave = async () => {
    await onSaveContext?.(form);
    setEditing(false);
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto bg-white">
      <Section title="Impact">
        <p className="text-sm font-semibold text-slate-900">{severity.name}</p>
        <p className="mt-1 text-xs leading-relaxed text-slate-600">
          {severity.description}
        </p>
      </Section>

      <Section title="Reported problem">
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
          {problem?.description || "No description provided."}
        </p>
      </Section>

      <Section
        title="Incident context"
        action={
          canEdit &&
          (editing ? (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2 py-1 text-xs font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60"
              >
                <Check className="h-3 w-3" />
                {saving ? "Saving" : "Save"}
              </button>
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="inline-flex h-6 w-6 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                aria-label="Cancel editing context"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 transition hover:text-blue-800"
            >
              <Pencil className="h-3 w-3" />
              {hasAnyContext ? "Edit" : "Add"}
            </button>
          ))
        }
      >
        {editing ? (
          <div className="space-y-2.5">
            {CONTEXT_FIELDS.map((field) => (
              <label key={field.key} className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">
                  {field.label}
                </span>
                <textarea
                  value={form[field.key]}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, [field.key]: e.target.value }))
                  }
                  rows={field.rows}
                  placeholder={field.placeholder}
                  className={`w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs text-slate-800 transition focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                    field.mono ? "font-mono" : ""
                  }`}
                />
              </label>
            ))}
          </div>
        ) : hasAnyContext ? (
          <dl className="space-y-2.5">
            {CONTEXT_FIELDS.map((field) =>
              context?.[field.key] ? (
                <div key={field.key}>
                  <dt className="text-xs font-medium text-slate-500">
                    {field.label}
                  </dt>
                  <dd
                    className={`mt-0.5 whitespace-pre-wrap text-xs leading-relaxed text-slate-800 ${
                      field.mono ? "font-mono" : ""
                    }`}
                  >
                    {context[field.key]}
                  </dd>
                </div>
              ) : null
            )}
          </dl>
        ) : (
          <p className="text-xs leading-relaxed text-slate-500">
            {canEdit
              ? "Add environment details, exact errors, and what you already tried. It saves session time."
              : "The customer has not added structured context yet. Ask for environment details and exact error text."}
          </p>
        )}
      </Section>

      <Section title="Classification">
        <dl>
          <Fact label="Lane" value={lane?.label} />
          <Fact
            label="Category"
            value={problem?.categoryId ? getCategoryLabel(problem.categoryId) : null}
          />
          <Fact label="Reported urgency" value={problem?.urgency} />
        </dl>
        {Array.isArray(problem?.tags) && problem.tags.length > 0 && (
          <div className="mt-2.5 flex flex-wrap gap-1">
            {problem.tags.map((tag) => (
              <span
                key={tag}
                className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-2xs text-slate-600"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </Section>

      <Section title="Participants">
        <dl>
          <Fact
            label="Customer"
            value={problem?.clientName || problem?.clientEmail}
          />
          <Fact label="Expert" value={problem?.helperName || "Unassigned"} />
        </dl>
      </Section>
    </div>
  );
}
